/**
 * テーマ色（#191、docs/77 §4、docs/10 §4.7）：個人設定の「表示」の節の1行。色の丸いチップ（藍・濃い青・青緑・濃い緑・墨）と色名を横に並べる。
 *   - 選んでいる色は ✓ と太い枠（色だけで伝えない）。製品の既定の色（現場力＝藍、営業力＝濃い青）の名前の下に「既定」
 *   - 押した瞬間に画面全体に当てて、人（accounts）に保存する（PUT /account/preferences）＝PC・スマホで同じ。保存できなければ前の色に戻して行の名前を朱の文に
 *   - 製品の既定の色を選んだら人の設定を消す（null＝製品の既定に従う。営業力と現場力の両方の会社に入っている人は、それぞれの既定の色になる）
 *   - 読み上げは radiogroup／radio（aria-checked）。← → ↑ ↓ で移って選ぶ（ふつうのラジオボタンと同じ）。押すところは 64×88px
 *   - 画面に出さない色（contract の THEME_HIDDEN＝色覚の確かめで朱と見分けにくかった濃い緑）は並べない。その色を使っている人には並べる
 *   - ダーク（#191 の2回目）では丸の色もダークの主色（contract の DARK_THEME）＝画面に出る色と同じ
 * 明るさ（ColorModeRow、#191 の2回目、docs/77 §8）：ライト／ダーク／端末に合わせる のセグメント。既定はライト。押した瞬間に当てて人に保存
 */
import { COLOR_MODES, DARK_THEME, THEME_MSG, themeChoices, type AccountPreferences, type ColorMode, type Me, type ThemeColor } from "@genbaryoku/contract";
import { useQueryClient } from "@tanstack/react-query";
import { useId, useRef, useState } from "react";
import { api, messageOf } from "../api.js";
import { ME_KEY, useMe } from "../auth.js";
import { applyTheme, productTheme } from "../lib/theme.js";
import { RowIcon } from "./GroupList.jsx";
import { useColorModeNow } from "./LightArea.jsx";
import { Segmented } from "./Segmented.jsx";

/**
 * menu＝右上の「アカウント」のメニューの中（#191 の直し、10/16 夜：射場さん「メニューを広げてもテーマが無い」）。
 *   丸は role="menuitemradio"（↓↑ はメニューが回す＝この行は ←→ で選ぶだけ）。押してもメニューは閉じない（文字サイズと同じ）
 */
export function ThemeColorRow({ menu = false }: { menu?: boolean } = {}) {
  const qc = useQueryClient();
  const me = useMe();
  const titleId = useId();
  const [err, setErr] = useState<string | undefined>();
  const btns = useRef<(HTMLButtonElement | null)[]>([]);
  const dark = useColorModeNow() === "dark";
  // 保存は1本ずつ・最後に選んだ色だけ（← → で続けて選んでも、答えの前後で古い色が残らない）
  const queue = useRef<{ running: boolean; next: { value: ThemeColor | null; prev: Me } | null }>({ running: false, next: null });
  if (!me.data) return null;
  const m = me.data;
  const def = productTheme(m.tenant.product);
  const saved = m.preferences?.themeColor ?? null;
  const value: ThemeColor = saved ?? def;
  const choices = themeChoices(saved);

  const flush = async () => {
    const q = queue.current;
    if (q.running) return;
    q.running = true;
    while (q.next) {
      const job = q.next;
      q.next = null;
      try {
        await api<AccountPreferences>("PUT", "/account/preferences", { themeColor: job.value });
      } catch (e) {
        if (!q.next) {
          // 後から選んだ色が無ければ、選ぶ前の色に戻す
          qc.setQueryData<Me>(ME_KEY, job.prev);
          applyTheme(job.prev.preferences?.themeColor ?? def);
          setErr(`${THEME_MSG.saveFailed}（${messageOf(e)}）`);
        }
      }
    }
    q.running = false;
  };

  const pick = (t: ThemeColor) => {
    if (t === value) return;
    setErr(undefined);
    const prev = qc.getQueryData<Me>(ME_KEY) ?? m;
    const next = t === def ? null : t;
    // すぐ当てる（ThemeHost も ME から同じ色を決める）
    qc.setQueryData<Me>(ME_KEY, { ...prev, preferences: { ...prev.preferences, themeColor: next } });
    applyTheme(t);
    queue.current.next = { value: next, prev: queue.current.next?.prev ?? prev };
    void flush();
  };

  const onKey = (e: React.KeyboardEvent, i: number) => {
    // メニューの中では ↓↑ はメニューの項目を移る（ここでは ←→ だけ）
    const d = e.key === "ArrowRight" || (!menu && e.key === "ArrowDown") ? 1 : e.key === "ArrowLeft" || (!menu && e.key === "ArrowUp") ? -1 : 0;
    if (!d) return;
    e.preventDefault();
    const j = (i + d + choices.length) % choices.length;
    const t = choices[j]!.key;
    pick(t);
    btns.current[j]?.focus();
  };

  return (
    <li className="grow grow-set grow-theme has-ico" data-testid={menu ? "menu-theme" : "profile-theme"}>
      <div className="growin">
        <span className="gtx">
          <span className="gtt" id={titleId}>
            {err ? <span className="err" data-testid="theme-error">{err}</span> : THEME_MSG.title}
          </span>
        </span>
        <span className="gico">
          <RowIcon k="palette" />
        </span>
        <div className="thchips" role={menu ? "group" : "radiogroup"} aria-labelledby={titleId}>
          {choices.map((t, i) => {
            const on = t.key === value;
            return (
              <button
                key={t.key}
                ref={(el) => {
                  btns.current[i] = el;
                }}
                type="button"
                role={menu ? "menuitemradio" : "radio"}
                aria-checked={on}
                tabIndex={menu ? -1 : on ? 0 : -1}
                className={"thchip" + (on ? " is-on" : "")}
                data-testid={menu ? `menu-theme-${t.key}` : `theme-${t.key}`}
                onClick={() => pick(t.key)}
                onKeyDown={(e) => onKey(e, i)}
              >
                <span className="thdot" style={{ background: dark ? DARK_THEME[t.key].primary : t.primary }} aria-hidden="true">
                  {on ? (
                    <svg viewBox="0 0 24 24">
                      <path d="M5 12.5l4.5 4.5L19 7.5" />
                    </svg>
                  ) : null}
                </span>
                <span className="thname">{t.label}</span>
                {t.key === def ? <span className="thdef">{THEME_MSG.defaultMark}</span> : null}
              </button>
            );
          })}
        </div>
      </div>
    </li>
  );
}

/** 明るさ（#191 の2回目）：ライト／ダーク／端末に合わせる。押した瞬間に ME を書き換え（ThemeHost が当てる）・人に保存。保存できなければ前に戻して朱の文 */
/** menu＝右上の「アカウント」のメニューの中（ボタンは role="menuitemradio"。押してもメニューは閉じない） */
export function ColorModeRow({ menu = false }: { menu?: boolean } = {}) {
  const qc = useQueryClient();
  const me = useMe();
  const titleId = useId();
  const [err, setErr] = useState<string | undefined>();
  const seq = useRef(0);
  if (!me.data) return null;
  const value: ColorMode = me.data.preferences?.colorMode ?? "light";
  const pick = async (v: ColorMode) => {
    if (v === value) return;
    setErr(undefined);
    const prev = qc.getQueryData<Me>(ME_KEY) ?? me.data!;
    // ライトは人の設定を消す（null＝既定のライト）
    const next = v === "light" ? null : v;
    qc.setQueryData<Me>(ME_KEY, { ...prev, preferences: { ...prev.preferences, colorMode: next } });
    const n = ++seq.current;
    try {
      await api<AccountPreferences>("PUT", "/account/preferences", { colorMode: next });
    } catch (e) {
      if (n !== seq.current) return; // 後から選び直した（そちらの答えを待つ）
      qc.setQueryData<Me>(ME_KEY, prev);
      setErr(`${THEME_MSG.modeSaveFailed}（${messageOf(e)}）`);
    }
  };
  return (
    <li className="grow grow-set grow-mode has-ico" data-testid={menu ? "menu-color-mode" : "profile-color-mode"}>
      <div className="growin">
        <span className="gtx">
          <span className="gtt" id={titleId}>
            {err ? <span className="err" data-testid="color-mode-error">{err}</span> : THEME_MSG.mode}
          </span>
        </span>
        <span className="gico">
          <RowIcon k="contrast" />
        </span>
        <div className="mdseg">
          <Segmented
            labelledBy={titleId}
            menu={menu}
            className="is-wide"
            value={value}
            onChange={(v) => void pick(v)}
            options={COLOR_MODES.map((m) => ({ value: m.key, label: m.label, testId: menu ? `menu-color-mode-${m.key}` : `color-mode-${m.key}` }))}
          />
        </div>
      </div>
    </li>
  );
}
